import EmptyState from "./empty-state.jsx";
import { apiFetch } from "./app-state.jsx";
import React from "react";
import "./services.css";
import { SiteHeader as Header, SiteFooter as Footer } from "./chrome.jsx";
import {
  FaArrowRight,
  FaCheck,
  FaUsers,
  FaHandshake,
  FaHouse,
  FaCode,
  FaMobileScreenButton,
  FaLaptopCode,
  FaXmark,
} from "react-icons/fa6";

import nexMoveLogo from "./assets/nexmove.webp";
import nexMoveLightLogo from "./assets/Nex move logo (2).png";
import diopzLogo from "./assets/diopz.png";
import stayawyLogo from "./assets/stayawy.png";

/* ------------------------- OUR BRANDS PAGE ------------------------- */

const fallbackProjects = [
  {
    number: "01",
    name: "NEX MOVE",
    meta: "LOGISTICS PLATFORM",
    title: "Move work|forward.",
    body: "A logistics platform for households and businesses, bringing booking, instant quotes and transport opportunities into one clear flow.",
    mark: "nex",
    layout: "copy-first",
    websiteUrl: "https://nexmove.in/home",
  },
  {
    number: "02",
    name: "Diopz",
    meta: "HRMS PLATFORM",
    title: "Manage every|employee journey.",
    body: "A central HRMS for attendance, payroll, leave, recruitment and employee management with AI support through Brigenza.",
    mark: "diopz",
    layout: "image-first",
    websiteUrl: "https://diopz.com/",
  },
  {
    number: "03",
    name: "Stayawy",
    meta: "TRAVEL AND STAYS",
    title: "Book better spaces|with less effort.",
    body: "A stay-booking app for discovering comfortable accommodation, comparing property details and managing the trip from one clear place.",
    mark: "stay",
    layout: "copy-first",
    websiteUrl: "https://stayawy.com/",
  },
];

function normalizeProject(project, index) {
  const title = Array.isArray(project.title)
    ? project.title
    : String(project.title || "").split("|");

  const images = Array.isArray(project.images)
    ? project.images.filter(Boolean)
    : [project.image, project.image2, project.image3].filter(Boolean);

  return {
    number: project.number || String(index + 1).padStart(2, "0"),
    name: project.name || "New Service",
    meta: project.meta || "SERVICE",
    title: title.length > 1 ? title : [title[0] || "New service", ""],
    body: project.body || "",
    image: project.image || "",
    images,
    mark: project.mark || "stay",
    layout: project.layout || (index % 2 === 1 ? "image-first" : "copy-first"),
    callout: project.callout || "",
    note: project.note || "",
    websiteUrl: project.websiteUrl || "",
  };
}

function getProjectLogo(project) {
  const name = String(project.name || "").trim().toLowerCase();
  const mark = String(project.mark || "").trim().toLowerCase();

  if (name === "nex move" || mark === "nex" || mark === "wide") {
    return nexMoveLogo;
  }
  if (name === "diopz" || mark === "diopz") {
    return diopzLogo;
  }
  if (name === "stayawy" || name === "stayway" || mark === "stay") {
    return stayawyLogo;
  }

  return stayawyLogo;
}

function getApiUrl() {
  return process.env.REACT_APP_API_URL?.replace(/\/+$/, "");
}

function getProjectUrl(project) {
  if (project.websiteUrl) return project.websiteUrl;

  const name = String(project.name || "").trim().toLowerCase();

  if (name === "nex move") return "https://nexmove.in/home";
  if (name === "diopz") return "https://diopz.com/";
  if (name === "stayawy" || name === "stayway") return "https://stayawy.com/";

  return "";
}

function BrandMark({ project }) {
  const isNexMove = getProjectLogo(project) === nexMoveLogo;
  return (
    <div className={`brand-mark mark-${project.mark}`}>
      <span aria-hidden="true">
        <img className={isNexMove ? "brand-logo-default" : undefined} src={getProjectLogo(project)} alt="" />
        {isNexMove && <img className="brand-logo-light" src={nexMoveLightLogo} alt="" />}
      </span>
      <strong>{project.name}</strong>
    </div>
  );
}

function ProjectCopy({ project }) {
  const projectUrl = getProjectUrl(project);

  return (
    <div className="project-copy">
      <div className="project-number">
        <strong>{project.number}</strong>
        <i />
      </div>

      <BrandMark project={project} />
      <p className="project-meta">{project.meta}</p>

      <h2>
        {project.title.map((line, index) => (
          <React.Fragment key={`${line}-${index}`}>
            <span className={index === project.title.length - 1 ? "accent" : ""}>
              {line}
            </span>
            {index < project.title.length - 1 && <br />}
          </React.Fragment>
        ))}
      </h2>

      <p className="project-body">{project.body}</p>

      {projectUrl && (
        <a
          className="project-button"
          href={projectUrl}
          target="_blank"
          rel="noreferrer"
        >
          View Project <FaArrowRight aria-hidden="true" />
        </a>
      )}
    </div>
  );
}

function ProjectVisual({ project }) {
  const logo = getProjectLogo(project);
  const image = project.image || project.images?.[0] || logo;
  const isLogo = image === logo;

  return (
    <figure className="project-visual">
      <div
        className={`project-image ${isLogo ? "logo-image-panel" : ""}`}
        aria-hidden="true"
      >
        <img src={image} alt="" />
      </div>
    </figure>
  );
}

function ProjectRow({ project }) {
  return (
    <article
      className={`project-row ${
        project.layout === "image-first" ? "image-first" : ""
      }`}
    >
      <ProjectCopy project={project} />
      <ProjectVisual project={project} />
    </article>
  );
}

export function BrandsPage() {
  const [projects, setProjects] = React.useState(() =>
    fallbackProjects.map(normalizeProject)
  );

  React.useEffect(() => {
    const apiUrl = getApiUrl();
    if (!apiUrl) return undefined;

    let cancelled = false;

    apiFetch(`${apiUrl}/brands`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load brands (${response.status})`);
        }
        return response.json();
      })
      .then((data) => {
        if (!cancelled && Array.isArray(data)) {
          setProjects(data.map(normalizeProject));
        }
      })
      .catch((error) => {
        console.error("SERVICES LOAD ERROR:", error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Header />
      <main className="services-page">
        {projects.length === 0 && <EmptyState />}
        {projects.map((project) => (
          <ProjectRow project={project} key={project.name} />
        ))}
      </main>
      <Footer />
    </>
  );
}

/* --------------------------- SERVICES PAGE -------------------------- */

const serviceOfferings = [
  {
    title: "Diopz HRMS",
    tone: "violet",
    Icon: FaUsers,
    body: "Simplify human resource management with one platform for employee records, attendance, leave, payroll, and HR operations.",
    features: [
      "Employee Management",
      "Attendance & Leave Tracking",
      "Payroll Management",
      "HR Reports & Analytics",
    ],
    includes: [
      "Centralized employee records",
      "Attendance and leave workflows",
      "Payroll management features",
      "HR reports and dashboards",
    ],
    bestFor:
      "Businesses that want to simplify HR operations and manage employee information efficiently.",
    details:
      "Diopz HRMS helps organizations organize employee information and streamline everyday human resource activities through a centralized management platform.",
  },
  {
    title: "CRM Solutions",
    tone: "green",
    Icon: FaHandshake,
    body: "Manage leads, customer relationships, sales pipelines, and follow-ups through a centralized CRM solution.",
    features: [
      "Lead Management",
      "Customer Database",
      "Sales Pipeline Tracking",
      "Reports & Analytics",
    ],
    includes: [
      "Lead and contact management",
      "Customer activity tracking",
      "Sales pipeline workflows",
      "Follow-up and performance reports",
    ],
    bestFor:
      "Businesses looking to improve customer relationships, organize enquiries, and manage sales opportunities.",
    details:
      "Our CRM solutions help teams centralize customer information, track sales opportunities, organize follow-ups, and improve visibility into customer interactions.",
  },
  {
    title: "Stayawy",
    tone: "cyan",
    Icon: FaHouse,
    body: "Deliver a streamlined digital experience through a platform designed around user needs and everyday workflows.",
    features: [
      "User-Friendly Interface",
      "Responsive Experience",
      "Organized Information",
      "Digital Workflow Support",
    ],
    includes: [
      "Modern user interface",
      "Responsive page layouts",
      "Feature and API integration",
      "Maintainable application structure",
    ],
    bestFor:
      "Users and businesses looking for a simple, accessible, and well-organized digital platform.",
    details:
      "Stayawy is presented as a digital platform service. Its exact features and integrations should reflect the actual Stayawy product and its business requirements.",
  },
  {
    title: "Web Development",
    tone: "violet",
    Icon: FaCode,
    body: "Build professional, responsive websites that communicate your brand and create a strong online presence.",
    features: [
      "Business Websites",
      "Landing Pages",
      "Responsive Web Design",
      "SEO-Friendly Structure",
    ],
    includes: [
      "Modern website layouts",
      "Mobile and tablet responsiveness",
      "Contact and enquiry forms",
      "Performance and SEO improvements",
    ],
    bestFor:
      "Startups, small businesses, and established companies that need a professional website.",
    details:
      "We develop modern business websites, landing pages, portfolios, and e-commerce experiences with a focus on responsive design, usability, performance, and maintainable code.",
  },
  {
    title: "Application Development",
    tone: "cyan",
    Icon: FaMobileScreenButton,
    body: "Develop applications that help businesses manage workflows, deliver digital services, and improve user experiences.",
    features: [
      "Web Applications",
      "Mobile-Friendly Interfaces",
      "API Integration",
      "Secure Authentication",
    ],
    includes: [
      "Interactive frontend screens",
      "Backend API integration",
      "Database connectivity",
      "Testing and deployment support",
    ],
    bestFor:
      "Businesses that need interactive applications to support their customers, employees, or internal processes.",
    details:
      "Our application development services focus on practical features, intuitive user experiences, reliable API integration, and maintainable application architecture.",
  },
  {
    title: "Custom Software & Web Apps",
    tone: "green",
    Icon: FaLaptopCode,
    body: "Turn unique business requirements into custom software and web applications built around your processes.",
    features: [
      "Custom Business Software",
      "Admin Dashboards",
      "Workflow Automation",
      "Third-Party Integrations",
    ],
    includes: [
      "Requirement analysis",
      "Custom feature development",
      "Database and API design",
      "Testing and maintenance support",
    ],
    bestFor:
      "Organizations that need tailored software to solve specific business challenges and streamline operations.",
    details:
      "We build custom software, web applications, internal tools, and management dashboards based on your requirements, helping connect workflows and support future business growth.",
  },
];

function ServiceCard({ service, onLearnMore }) {
  const Icon = service.Icon;

  return (
    <article className={`service-card service-card-${service.tone}`}>
      <div className="service-card-head">
        <div className="service-icon" aria-hidden="true">
          <Icon />
        </div>
        <h2>{service.title}</h2>
      </div>

      <p className="project-body">{service.body}</p>

      <ul className="feature-list">
        {service.features.map((feature) => (
          <li key={feature}>
            <span>
              <FaCheck aria-hidden="true" />
            </span>
            {feature}
          </li>
        ))}
      </ul>

      <button
        className="service-learn"
        type="button"
        onClick={() => onLearnMore(service)}
      >
        Learn More <FaArrowRight aria-hidden="true" />
      </button>
    </article>
  );
}

function ServiceDetailsModal({ service, onClose }) {
  if (!service) return null;

  const Icon = service.Icon;

  return (
    <div className="service-modal-backdrop" onClick={onClose}>
      <section
        className="service-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="service-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          className="service-modal-close"
          type="button"
          aria-label="Close details"
          onClick={onClose}
        >
          <FaXmark aria-hidden="true" />
        </button>

        <div className="service-modal-scroll">
        <div className="service-modal-head">
          <div
            className={`service-modal-icon service-card-${service.tone}`}
            aria-hidden="true"
          >
            <Icon />
          </div>
          <div>
            <h2 id="service-modal-title">{service.title}</h2>
          </div>
        </div>

        <p>{service.details}</p>

        <div className="service-modal-grid">
          <article>
            <h3>Core Services</h3>
            <ul className="feature-list">
              {service.features.map((feature) => (
                <li key={feature}>
                  <span>
                    <FaCheck aria-hidden="true" />
                  </span>
                  {feature}
                </li>
              ))}
            </ul>
          </article>

          <article>
            <h3>What's Included</h3>
            <ul className="feature-list">
              {service.includes.map((item) => (
                <li key={item}>
                  <span>
                    <FaCheck aria-hidden="true" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </article>
        </div>

        <div className="service-modal-note">
          <h3>Best For</h3>
          <p>{service.bestFor}</p>
        </div>
        </div>
      </section>
    </div>
  );
}

function App() {
  const [activeService, setActiveService] = React.useState(null);

  return (
    <>
      <Header />
      <main className="services-page">
        <section className="services-hero">
          <p className="project-meta">OUR SERVICES</p>
          <h1>Digital services for modern businesses.</h1>
          <p>
            From HR management and CRM solutions to websites and custom
            applications, we help businesses build better digital experiences.
          </p>
        </section>

        <section className="services-list" aria-label="Hallef services">
          {serviceOfferings.map((service) => (
            <ServiceCard
              key={service.title}
              service={service}
              onLearnMore={setActiveService}
            />
          ))}
        </section>

        <ServiceDetailsModal
          service={activeService}
          onClose={() => setActiveService(null)}
        />
      </main>
      <Footer />
    </>
  );
}

export default App;
